import { ArrowRight24Icon } from "@kaltura/ds-react-icons";
import { translate } from "@kaltura/ds-react-utils";
import { useTheme } from "@kaltura/mediaspace-shared-styled";
import { useMediaQuery } from "@kaltura/mediaspace-shared-utils";
import styled from "@emotion/styled";
import { useFormContext } from "react-hook-form";
import { FormValues } from "../types";
import { DEFAULT_TIME_FORMAT, getInitialTimes } from "./utils";
import { Typography } from "@kaltura/ds-react-components";
import PickersUnit from "./pickers-unit/PickersUnit";
import { useContext } from "react";
import { ConfigContext } from "@kaltura/mediaspace-shared-data-kms-config";

export const StyledPickersWrapper = styled("div")(({ theme }) => ({
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    justifyContent: "space-between",
    [theme.breakpoints.down(theme.breakpoints.values.md)]: {
        flexDirection: "column",
    },
}));

const StyledTypography = styled(Typography)(({ theme }) => ({
    color: theme.kaltura.palette.danger.main,
    marginTop: theme.spacing(1),
}));

const baseClassName = "kms-ds-create-room-modal-form-pickers";
const roomStartTimeDateName = "startTimeDate" as const;
const roomStartTimeTimeName = "startTimeTime" as const;
const roomEndTimeDateName = "endTimeDate" as const;
const roomEndTimeTimeName = "endTimeTime" as const;

const PickersSection = () => {
    const configContext = useContext(ConfigContext);
    const dateFormat = configContext.application.shortDateFormat || "DD/MM/YYYY";

    const { formState } = useFormContext<FormValues>();
    const { startDate, startTime, endDate, endTime } = getInitialTimes(dateFormat, DEFAULT_TIME_FORMAT);

    const theme = useTheme();
    const isXsScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.sm));
    const isSmScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.md));
    const showArrow = !isXsScreen && !isSmScreen;

    // Get form error if any
    const errors = formState.errors;
    const error =
        errors[roomStartTimeDateName]?.message
        || errors[roomEndTimeDateName]?.message
        || errors[roomStartTimeTimeName]?.message
        || errors[roomEndTimeTimeName]?.message;

    return (
        <>
            <StyledPickersWrapper className={`${baseClassName}`}>
                <PickersUnit
                    dateFieldName={roomStartTimeDateName}
                    timeFieldName={roomStartTimeTimeName}
                    dateDefaultValue={startDate}
                    timeDefaultValue={startTime}
                    label={translate("Start time")}
                    dateFormat={dateFormat}
                    timeFormat={DEFAULT_TIME_FORMAT}
                />

                {showArrow && <ArrowRight24Icon style={{ marginTop: "27px" }} />}

                <PickersUnit
                    dateFieldName={roomEndTimeDateName}
                    timeFieldName={roomEndTimeTimeName}
                    dateDefaultValue={endDate}
                    timeDefaultValue={endTime}
                    label={translate("End time")}
                    dateFormat={dateFormat}
                    timeFormat={DEFAULT_TIME_FORMAT}
                />
            </StyledPickersWrapper>

            {error && (
                <StyledTypography
                    variant={"formError"}
                    className={`${baseClassName}-error`}
                    role="alert"
                    aria-live="polite"
                >
                    {error}
                </StyledTypography>
            )}
        </>
    );
};

export default PickersSection;
